import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const formatCOP = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
const formatCOPCorto = (v) => {
  if (v >= 1000000) return `$${(v/1000000).toFixed(1)}M`;
  if (v >= 1000) return `$${(v/1000).toFixed(0)}K`;
  return `$${v}`;
};

const COLORES = ['#00E5FF', '#6366F1', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

const estadoPagoConfig = {
  pagado: { color: '#10B981', bg: 'rgba(16,185,129,0.1)' },
  pendiente: { color: '#F59E0B', bg: 'rgba(245,158,11,0.1)' },
  vencido: { color: '#EF4444', bg: 'rgba(239,68,68,0.1)' },
};

const sb = {
  num: { fontFamily:'Poppins', fontSize:'24px', fontWeight:800, color:'#fff', lineHeight:1.1 },
  rot: { fontSize:'11.5px', color:'rgba(255,255,255,0.45)', marginTop:'2px' },
  tit: { fontSize:'12px', fontWeight:700, textTransform:'uppercase', letterSpacing:'.8px',
         color:'rgba(255,255,255,0.55)', marginBottom:'8px' },
  td:  { padding:'7px 4px', fontSize:'13px', color:'#E2E8F0' },
  cat: { display:'inline-block', marginLeft:'8px', fontSize:'10.5px', padding:'1px 7px',
         borderRadius:'999px', background:'rgba(255,255,255,0.08)', color:'rgba(255,255,255,0.5)' },
  nada:{ fontSize:'13px', color:'rgba(255,255,255,0.4)', padding:'8px 0' },
  subtit:{ display:'block', fontWeight:400, fontSize:'11.5px', textTransform:'none',
           letterSpacing:0, color:'rgba(255,255,255,0.4)', marginTop:'3px' },
};

export default function Reportes() {
  const hoy = new Date();
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [resumen, setResumen] = useState(null);
  const [tendencia, setTendencia] = useState([]);
  const [porPlan, setPorPlan] = useState([]);
  const [topClientes, setTopClientes] = useState([]);
  const [cargando, setCargando]       = useState(true);
  const [rentabilidad, setRentabilidad] = useState([]);
  const [mostrarRent, setMostrarRent]   = useState(false);
  // Qué busca la gente en la tienda. Va con su propio periodo porque no se mira con el
  // mismo ojo que la cartera: las búsquedas de esta semana dicen qué comprar YA.
  const [busq, setBusq]         = useState(null);
  const [diasBusq, setDiasBusq] = useState('30');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [resR, resT, resP, resC, resRent] = await Promise.all([
        api.get(`/api/reportes/resumen-general?mes=${mes}&anio=${anio}`),
        api.get(`/api/reportes/tendencia-anual?anio=${anio}`),
        api.get('/api/reportes/clientes-por-plan'),
        api.get(`/api/reportes/top-clientes?mes=${mes}&anio=${anio}`),
        api.get('/api/reportes/rentabilidad-proveedores-red'),
      ]);
      setResumen(resR.data);
      setTendencia(resT.data.map(d => ({ ...d, mes: MESES_CORTOS[d.mes - 1] })));
      setPorPlan(resP.data);
      setTopClientes(resC.data);
      setRentabilidad(resRent.data);
    } catch (e) { console.error(e); }
    finally { setCargando(false); }
  }, [mes, anio]);

  useEffect(() => { cargar(); }, [cargar]);

  // Aparte de la carga principal: si esto falla —o si la tabla todavía no existe porque no
  // se ha corrido la migración— el resto de Reportes tiene que seguir funcionando igual.
  useEffect(() => {
    api.get(`/api/reportes/busquedas-tienda?dias=${diasBusq}`)
      .then(r => setBusq(r.data))
      .catch(() => setBusq(null));
  }, [diasBusq]);

  const balance = parseFloat(resumen?.resumen?.balance || 0);

  if (cargando) return (
    <Layout>
      <div style={{ textAlign: 'center', marginTop: '80px', color: 'rgba(255,255,255,0.4)' }}>
        Cargando reportes...
      </div>
    </Layout>
  );

  return (
    <Layout>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.titulo}>Reportes</h1>
          <p style={s.sub}>Vista ejecutiva del negocio</p>
        </div>
        <div style={s.selector}>
          <select style={s.selectMes} value={mes} onChange={e => setMes(parseInt(e.target.value))}>
            {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select style={s.selectAnio} value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
            {[anio-1, anio, anio+1].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* KPIs principales */}
      <div style={s.kpiGrid}>
        <div style={s.kpiCard}>
          <div style={s.kpiIcon}>👥</div>
          <div style={{ ...s.kpiVal, color: '#00E5FF' }}>{resumen?.clientes?.activos || 0}</div>
          <div style={s.kpiLabel}>Clientes activos</div>
          <div style={s.kpiSub}>{resumen?.clientes?.morosos || 0} en mora · {resumen?.clientes?.total || 0} total</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiIcon}>💳</div>
          <div style={{ ...s.kpiVal, color: '#10B981' }}>{formatCOP(resumen?.cartera?.cobrado)}</div>
          <div style={s.kpiLabel}>Cobrado en suscripciones</div>
          <div style={s.kpiSub}>{resumen?.cartera?.pagados || 0} pagados · {resumen?.cartera?.pendientes || 0} pendientes</div>
        </div>
        <div style={s.kpiCard}>
          <div style={s.kpiIcon}>🛒</div>
          <div style={{ ...s.kpiVal, color: '#6366F1' }}>{formatCOP(resumen?.ventas?.total_ventas)}</div>
          <div style={s.kpiLabel}>Ventas y servicios</div>
          <div style={s.kpiSub}>{resumen?.ventas?.num_ventas || 0} transacciones del mes</div>
        </div>
        <div style={{ ...s.kpiCard, border: `1px solid ${balance >= 0 ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}` }}>
          <div style={s.kpiIcon}>{balance >= 0 ? '💰' : '⚠'}</div>
          <div style={{ ...s.kpiVal, color: balance >= 0 ? '#10B981' : '#EF4444' }}>{formatCOP(balance)}</div>
          <div style={s.kpiLabel}>Balance del mes</div>
          <div style={s.kpiSub}>Ingresos {formatCOP(resumen?.resumen?.total_ingresos)} · Egresos {formatCOP(resumen?.resumen?.total_egresos)}</div>
        </div>
      </div>

      {/* Gráficas fila 1 */}
      <div className="layout-2col" style={s.chartsRow}>
        {/* Tendencia anual */}
        <div style={s.chartCard}>
          <h3 style={s.chartTitulo}>Ingresos vs Egresos — {anio}</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={tendencia} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="mes" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={formatCOPCorto} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: '#1a2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                formatter={(val, name) => [formatCOP(val), name]}
              />
              <Legend wrapperStyle={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }} />
              <Bar dataKey="suscripciones" name="Suscripciones" fill="#00E5FF" radius={[3,3,0,0]} />
              <Bar dataKey="ventas" name="Ventas" fill="#6366F1" radius={[3,3,0,0]} />
              <Bar dataKey="otros_ingresos" name="Otros ingresos" fill="#10B981" radius={[3,3,0,0]} />
              <Bar dataKey="egresos" name="Egresos" fill="#EF4444" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Clientes por plan */}
        <div style={s.chartCard}>
          <h3 style={s.chartTitulo}>Clientes por plan</h3>
          {porPlan.length === 0 ? (
            <div style={s.chartVacio}>Sin datos</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={porPlan} dataKey="total" nameKey="plan" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                    {porPlan.map((_, i) => <Cell key={i} fill={COLORES[i % COLORES.length]} />)}
                  </Pie>
                  <Tooltip
                    contentStyle={{ background: '#1a2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
                    formatter={(val, name) => [`${val} clientes`, name]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={s.leyenda}>
                {porPlan.map((p, i) => (
                  <div key={i} style={s.leyendaItem}>
                    <span style={{ ...s.leyendaDot, background: COLORES[i % COLORES.length] }} />
                    <span style={s.leyendaLabel}>{p.plan}</span>
                    <span style={s.leyendaVal}>{p.total} clientes</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Línea de tendencia */}
      <div style={s.chartCardFull}>
        <h3 style={s.chartTitulo}>Línea de suscripciones cobradas — {anio}</h3>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={tendencia} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
            <XAxis dataKey="mes" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={formatCOPCorto} tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{ background: '#1a2235', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '12px' }}
              formatter={(val) => [formatCOP(val), 'Cobrado']}
            />
            <Line type="monotone" dataKey="suscripciones" name="Suscripciones" stroke="#00E5FF" strokeWidth={2} dot={{ fill: '#00E5FF', r: 4 }} activeDot={{ r: 6 }} />
            <Line type="monotone" dataKey="total_ingresos" name="Total ingresos" stroke="#10B981" strokeWidth={2} strokeDasharray="5 5" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── QUÉ BUSCAN LOS CLIENTES EN LA TIENDA ──
          La tabla de la derecha es la que vale plata: son búsquedas que salieron VACÍAS,
          o sea gente que quiso comprar algo que no está en el catálogo. Antes esa
          información se perdía en el momento en que el cliente cerraba la página. */}
      {busq && (
        <div style={s.chartCardFull}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                        flexWrap:'wrap', gap:'10px' }}>
            <h3 style={{ ...s.chartTitulo, marginBottom:0 }}>🔎 Qué buscan tus clientes en la tienda</h3>
            <select value={diasBusq} onChange={e => setDiasBusq(e.target.value)}
              style={{ background:'#1a2235', color:'#fff', border:'1px solid rgba(255,255,255,0.15)',
                       borderRadius:'8px', padding:'7px 11px', fontSize:'13px' }}>
              <option value="7">Últimos 7 días</option>
              <option value="30">Últimos 30 días</option>
              <option value="90">Últimos 90 días</option>
            </select>
          </div>

          <div style={{ display:'flex', gap:'22px', flexWrap:'wrap', margin:'14px 0 6px' }}>
            <div><div style={sb.num}>{busq.total}</div><div style={sb.rot}>búsquedas</div></div>
            <div><div style={sb.num}>{busq.distintas}</div><div style={sb.rot}>frases distintas</div></div>
            <div>
              <div style={{ ...sb.num, color: busq.sin_resultados ? '#FCA5A5' : '#86EFAC' }}>
                {busq.sin_resultados}
              </div>
              <div style={sb.rot}>sin resultados</div>
            </div>
          </div>

          {busq.total === 0 ? (
            <div style={{ color:'rgba(255,255,255,0.45)', fontSize:'13px', padding:'14px 0' }}>
              Todavía no hay búsquedas en este periodo. Se van guardando solas a medida que
              la gente use la tienda.
            </div>
          ) : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(min(100%,320px),1fr))', gap:'20px', marginTop:'8px' }}>
              <div>
                <div style={sb.tit}>Lo más buscado</div>
                {busq.top.length === 0 ? <div style={sb.nada}>—</div> : (
                  <table style={s.table}><tbody>
                    {busq.top.map((b,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                        <td style={sb.td}>{b.texto}</td>
                        <td style={{ ...sb.td, textAlign:'right', whiteSpace:'nowrap' }}>
                          <b>{b.veces}</b> {b.veces === 1 ? 'vez' : 'veces'}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>

              <div>
                <div style={{ ...sb.tit, color:'#FCA5A5' }}>
                  Se fueron con las manos vacías
                  <span style={sb.subtit}>La búsqueda no les mostró NADA</span>
                </div>
                {busq.vacias.length === 0 ? (
                  <div style={sb.nada}>Ninguna búsqueda salió vacía. 👌</div>
                ) : (
                  <table style={s.table}><tbody>
                    {busq.vacias.map((b,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                        <td style={sb.td}>
                          {b.texto}
                          {b.categoria && <span style={sb.cat}>{b.categoria}</span>}
                        </td>
                        <td style={{ ...sb.td, textAlign:'right', whiteSpace:'nowrap', color:'#FCA5A5' }}>
                          <b>{b.veces}</b> {b.veces === 1 ? 'vez' : 'veces'}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>

              {/* La tercera lista, y la más rentable de leer.
                  Son palabras que NO existen en el catálogo aunque el cliente haya
                  alcanzado a ver algo: quien busca «nevera samsung» ve los Samsung que sí
                  hay, pero la nevera que venía a comprar no está. Sin esta columna esa
                  demanda no aparecía por ningún lado. */}
              <div>
                <div style={{ ...sb.tit, color:'#FDE047' }}>
                  Te lo pidieron y no lo tienes
                  <span style={sb.subtit}>Palabras que no existen en tu catálogo</span>
                </div>
                {(!busq.sin_catalogo || busq.sin_catalogo.length === 0) ? (
                  <div style={sb.nada}>Nada por ahora.</div>
                ) : (
                  <table style={s.table}><tbody>
                    {busq.sin_catalogo.map((b,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                        <td style={sb.td}>{b.texto}</td>
                        <td style={{ ...sb.td, textAlign:'right', whiteSpace:'nowrap', color:'#FDE047' }}>
                          <b>{b.veces}</b> {b.veces === 1 ? 'vez' : 'veces'}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                )}
              </div>
            </div>
          )}

          {/* Los correos que deja la gente en la tienda.
              El formulario promete «recibe ofertas exclusivas» y hasta ahora se guardaban en
              una tabla que ninguna pantalla leía: gente esperando ofertas que nadie podía
              mandarle. */}
          {busq.suscriptores && (
            <div style={{ marginTop:'18px', paddingTop:'16px', borderTop:'1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ ...sb.tit, color:'#86EFAC' }}>
                Correos para ofertas
                <span style={sb.subtit}>
                  {busq.suscriptores.total} en total
                  {busq.suscriptores.nuevos > 0 && ` · ${busq.suscriptores.nuevos} nuevos en ${diasBusq} días`}
                  {' '}· los dejaron pidiendo que les avises de las ofertas
                </span>
              </div>
              {busq.suscriptores.total === 0 ? (
                <div style={sb.nada}>Nadie se ha suscrito todavía.</div>
              ) : (
                <>
                  <button
                    style={{ marginBottom:'10px', padding:'6px 12px', fontSize:'12.5px',
                             borderRadius:'8px', border:'1px solid rgba(134,239,172,0.3)',
                             background:'rgba(134,239,172,0.1)', color:'#86EFAC', cursor:'pointer' }}
                    onClick={() => navigator.clipboard
                      .writeText(busq.suscriptores.lista.map(x => x.email).join(', '))
                      .then(() => alert(`${busq.suscriptores.lista.length} correos copiados. Pégalos en «Para» o «CCO» de tu correo.`))
                      .catch(() => alert('No se pudieron copiar. Selecciónalos de la lista.'))}>
                    📋 Copiar los {busq.suscriptores.lista.length} correos
                  </button>
                  <table style={s.table}><tbody>
                    {busq.suscriptores.lista.slice(0, 25).map((x, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={sb.td}>{x.email}</td>
                        <td style={{ ...sb.td, textAlign: 'right', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.4)' }}>
                          {new Date(x.creado_en).toLocaleDateString('es-CO')}
                        </td>
                      </tr>
                    ))}
                  </tbody></table>
                  {busq.suscriptores.lista.length > 25 && (
                    <div style={sb.nada}>y {busq.suscriptores.lista.length - 25} más — el botón los copia todos</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Top clientes del mes */}
      <div style={s.chartCardFull}>
        <h3 style={s.chartTitulo}>Estado de clientes activos — {MESES[mes-1]} {anio}</h3>
        <div style={s.topTabla}>
          <table style={s.table}>
            <thead>
              <tr style={s.thead}>
                <th style={s.th}>#</th>
                <th style={s.th}>Cliente</th>
                <th style={s.th}>Plan</th>
                <th style={s.th}>Valor mensual</th>
                <th style={s.th}>Estado del cobro</th>
                <th style={s.th}>Fecha de pago</th>
              </tr>
            </thead>
            <tbody>
              {topClientes.length === 0 ? (
                <tr><td colSpan={6} style={s.vacio}>No hay datos para este período</td></tr>
              ) : topClientes.map((c, i) => {
                const cfg = estadoPagoConfig[c.estado_pago] || { color: 'rgba(255,255,255,0.3)', bg: 'rgba(255,255,255,0.05)' };
                return (
                  <tr key={i} style={s.tr}>
                    <td style={{ ...s.td, color: 'rgba(255,255,255,0.3)', fontSize: '12px' }}>{i+1}</td>
                    <td style={s.td}><span style={s.clienteNombre}>{c.nombre}</span></td>
                    <td style={s.td}><span style={s.planTag}>{c.plan || '—'}</span></td>
                    <td style={s.td}><span style={s.montoTag}>{c.precio_mensual ? formatCOP(c.precio_mensual) : '—'}</span></td>
                    <td style={s.td}>
                      {c.estado_pago ? (
                        <span style={{ ...s.estadoBadge, color: cfg.color, background: cfg.bg }}>
                          {c.estado_pago}
                        </span>
                      ) : <span style={s.sinCobro}>Sin cobro generado</span>}
                    </td>
                    <td style={s.td}>
                      <span style={s.fechaTag}>{c.fecha_pago ? new Date(c.fecha_pago).toLocaleDateString('es-CO') : '—'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cartera del mes resumen */}
      <div className="layout-2col" style={s.chartsRow}>
        <div style={s.chartCard}>
          <h3 style={s.chartTitulo}>Cartera {MESES[mes-1]} {anio}</h3>
          <div style={s.carteraStats}>
            {[
              { label: 'Pagados', val: resumen?.cartera?.pagados || 0, color: '#10B981' },
              { label: 'Pendientes', val: resumen?.cartera?.pendientes || 0, color: '#F59E0B' },
              { label: 'Vencidos', val: resumen?.cartera?.vencidos || 0, color: '#EF4444' },
            ].map(st => (
              <div key={st.label} style={s.carteraItem}>
                <div style={{ ...s.carteraVal, color: st.color }}>{st.val}</div>
                <div style={s.carteraLabel}>{st.label}</div>
              </div>
            ))}
          </div>
          <div style={s.carteraMontos}>
            <div style={s.carteraMonto}>
              <span style={s.carteraMontoLabel}>Cobrado</span>
              <span style={{ color: '#10B981', fontWeight: '700' }}>{formatCOP(resumen?.cartera?.cobrado)}</span>
            </div>
            <div style={s.carteraMonto}>
              <span style={s.carteraMontoLabel}>Pendiente</span>
              <span style={{ color: '#F59E0B', fontWeight: '700' }}>{formatCOP(resumen?.cartera?.pendiente)}</span>
            </div>
          </div>
        </div>

        <div style={s.chartCard}>
          <h3 style={s.chartTitulo}>Ventas y servicios {MESES[mes-1]} {anio}</h3>
          <div style={s.carteraStats}>
            {[
              { label: 'Servicios', val: formatCOP(resumen?.ventas?.servicios), color: '#00E5FF' },
              { label: 'Productos', val: formatCOP(resumen?.ventas?.productos), color: '#6366F1' },
              /* Servicios y Productos van SIN descontar; el total de arriba va con el
                 descuento ya restado. Sin esta cifra las dos no cuadran y no se ve por qué. */
              ...(Number(resumen?.ventas?.descuentos) > 0
                ? [{ label: 'Descuentos dados', val: '−' + formatCOP(resumen.ventas.descuentos), color: '#FDBA74' }]
                : []),
              { label: 'Número de ventas', val: resumen?.ventas?.num_ventas || 0, color: '#10B981' },
            ].map(st => (
              <div key={st.label} style={s.carteraItem}>
                <div style={{ ...s.carteraVal, color: st.color, fontSize: '16px' }}>{st.val}</div>
                <div style={s.carteraLabel}>{st.label}</div>
              </div>
            ))}
          </div>
          <div style={s.carteraMontos}>
            <div style={s.carteraMonto}>
              <span style={s.carteraMontoLabel}>Total ingresos del mes</span>
              <span style={{ color: '#10B981', fontWeight: '700' }}>{formatCOP(resumen?.resumen?.total_ingresos)}</span>
            </div>
            <div style={s.carteraMonto}>
              <span style={s.carteraMontoLabel}>Total egresos del mes</span>
              <span style={{ color: '#EF4444', fontWeight: '700' }}>{formatCOP(resumen?.resumen?.total_egresos)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Rentabilidad Proveedores de Red */}
      {rentabilidad.length > 0 && (
        <div style={s.chartCardFull}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
            <h3 style={s.chartTitulo}>📡 Rentabilidad por Proveedor de Red</h3>
            <button
              style={{ background:'none', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'8px', padding:'5px 12px', color:'rgba(255,255,255,0.6)', fontSize:'12px', cursor:'pointer' }}
              onClick={()=>setMostrarRent(p=>!p)}>
              {mostrarRent ? '▲ Ocultar detalle' : '▼ Ver detalle por cliente'}
            </button>
          </div>
          {/* Resumen por proveedor */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(min(100%,280px),1fr))', gap:'12px', marginBottom: mostrarRent ? '20px' : 0 }}>
            {rentabilidad.map(prov => (
              <div key={prov.proveedor_id} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:'12px', padding:'16px' }}>
                <div style={{ fontSize:'14px', fontWeight:'700', color:'#fff', marginBottom:'12px' }}>
                  🌐 {prov.proveedor}
                  <span style={{ fontSize:'11px', color:'rgba(255,255,255,0.4)', fontWeight:'400', marginLeft:'8px' }}>{prov.tipo_red}</span>
                </div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'8px', textAlign:'center' }}>
                  {[
                    { label:'Ingresos', val: formatCOP(prov.total_ingresos), color:'#22C55E' },
                    { label:'Costos', val: formatCOP(prov.total_costos), color:'#FCA5A5' },
                    { label:'Utilidad', val: formatCOP(prov.total_utilidad), color: prov.total_utilidad >= 0 ? '#00E5FF' : '#EF4444' },
                  ].map(st => (
                    <div key={st.label} style={{ background:'rgba(0,0,0,0.2)', borderRadius:'8px', padding:'8px' }}>
                      <div style={{ fontSize:'13px', fontWeight:'700', color:st.color }}>{st.val}</div>
                      <div style={{ fontSize:'10px', color:'rgba(255,255,255,0.4)', marginTop:'2px' }}>{st.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.4)', marginTop:'10px', textAlign:'center' }}>
                  {prov.clientes.length} cliente{prov.clientes.length !== 1 ? 's' : ''} · tarifa {formatCOP(prov.tarifa_por_cliente)}/mes
                </div>
              </div>
            ))}
          </div>
          {/* Detalle por cliente */}
          {mostrarRent && (
            <div style={s.topTabla}>
              <table style={s.table}>
                <thead><tr style={s.thead}>
                  <th style={s.th}>Proveedor</th>
                  <th style={s.th}>Cliente</th>
                  <th style={s.th}>Plan</th>
                  <th style={s.th}>Estado</th>
                  <th style={s.th}>Ingreso</th>
                  <th style={s.th}>Costo red</th>
                  <th style={s.th}>Utilidad neta</th>
                </tr></thead>
                <tbody>
                  {rentabilidad.flatMap(prov => prov.clientes.map((c,i) => (
                    <tr key={c.cliente_id} style={s.tr}>
                      {i === 0 && <td style={{ ...s.td, color:'rgba(255,255,255,0.5)', fontSize:'12px' }} rowSpan={prov.clientes.length}>{prov.proveedor}</td>}
                      <td style={s.td}><strong style={{color:'#fff'}}>{c.cliente}</strong></td>
                      <td style={s.td}><span style={s.planTag}>{c.plan || '—'}</span></td>
                      <td style={s.td}>
                        <span style={{ fontSize:'11px', fontWeight:'600', color: c.estado==='activo'?'#22C55E':'#FCA5A5' }}>{c.estado}</span>
                      </td>
                      <td style={s.td}><span style={{ color:'#22C55E', fontWeight:'600' }}>{formatCOP(c.ingreso)}</span></td>
                      <td style={s.td}><span style={{ color:'#FCA5A5' }}>{formatCOP(c.costo)}</span></td>
                      <td style={s.td}>
                        <span style={{ fontWeight:'700', color: c.utilidad >= 0 ? '#00E5FF' : '#EF4444' }}>{formatCOP(c.utilidad)}</span>
                      </td>
                    </tr>
                  )))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

    </Layout>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' },
  titulo: { fontSize: '24px', fontWeight: '700', marginBottom: '4px' },
  sub: { color: 'rgba(255,255,255,0.4)', fontSize: '14px' },
  selector: { display: 'flex', gap: '8px' },
  selectMes: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '13px', outline: 'none', cursor: 'pointer' },
  selectAnio: { background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 12px', color: '#fff', fontSize: '13px', outline: 'none', cursor: 'pointer' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: '14px', marginBottom: '24px' },
  kpiCard: { minWidth: 0, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: 'clamp(14px,4vw,20px)' },
  kpiIcon: { fontSize: '24px', marginBottom: '8px' },
  // En el celular van de dos en dos y «$ 1.650.000» a 24 px no cabía en media pantalla
  kpiVal: { fontSize: 'clamp(18px,5vw,24px)', fontWeight: '700', marginBottom: '4px', overflowWrap: 'anywhere' },
  kpiLabel: { fontSize: '13px', color: 'rgba(255,255,255,0.7)', fontWeight: '500' },
  kpiSub: { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  chartsRow: { display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: '16px', marginBottom: '16px' },
  chartCard: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px' },
  chartCardFull: { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '20px', marginBottom: '16px' },
  chartTitulo: { fontSize: '14px', fontWeight: '600', color: 'rgba(255,255,255,0.8)', marginBottom: '16px' },
  chartVacio: { height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
  leyenda: { display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' },
  leyendaItem: { display: 'flex', alignItems: 'center', gap: '8px' },
  leyendaDot: { width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0 },
  leyendaLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', flex: 1 },
  leyendaVal: { fontSize: '12px', fontWeight: '600', color: '#fff' },
  topTabla: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thead: { background: 'rgba(255,255,255,0.04)' },
  th: { padding: '10px 14px', textAlign: 'left', fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', letterSpacing: '0.5px', textTransform: 'uppercase' },
  tr: { borderTop: '1px solid rgba(255,255,255,0.05)' },
  td: { padding: '12px 14px', verticalAlign: 'middle' },
  vacio: { padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '13px' },
  clienteNombre: { fontSize: '13px', fontWeight: '500' },
  planTag: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: '20px' },
  montoTag: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  estadoBadge: { fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '20px', textTransform: 'capitalize' },
  sinCobro: { fontSize: '11px', color: 'rgba(255,255,255,0.25)' },
  fechaTag: { fontSize: '12px', color: 'rgba(255,255,255,0.4)' },
  carteraStats: { display: 'flex', gap: '12px', marginBottom: '16px' },
  carteraItem: { flex: 1, background: 'rgba(255,255,255,0.04)', borderRadius: '10px', padding: '12px', textAlign: 'center' },
  carteraVal: { fontSize: '20px', fontWeight: '700' },
  carteraLabel: { fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  carteraMontos: { display: 'flex', flexDirection: 'column', gap: '8px' },
  carteraMonto: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' },
  carteraMontoLabel: { fontSize: '12px', color: 'rgba(255,255,255,0.5)' },
};
